<template>
    <v-container fluid class="pa-0 flex-grow-1 d-flex flex-column" style="overflow: hidden">
        <uv-filtered-grid :field-items="Object.values(filterFields)" :items="colorItems" :name="activityConfigName" :configurationTitle="$t('labels.profileActivitySettings')">
            <template #workplace>
                <uv-autocomplete :items="workplaceData.items" :loading="workplaceData.busy"
                    v-model="localQuery.workplace_id" :label="$t('labels.location')" search-field chips small-chips
                    deletable-chips multiple clearable @focus="getWorkplaces()" dense @change="getActivities()">
                    <template #prepend-inner>
                        <uv-button no-margin icon color="info" :title="$t('saveSearch')" @click.stop.native="saveQuery">
                            <v-icon size="28">{{ icons.mdiMapMarker }}</v-icon>
                        </uv-button>
                    </template>
                </uv-autocomplete>
            </template>
            <template #agent>
                <uv-autocomplete :items="agentData.items" :loading="agentData.busy" v-model="localQuery.agent_id"
                    :label="$t('labels.inCharge')" search-field chips small-chips deletable-chips multiple clearable
                    @focus="getAgents()" dense @change="getActivities()">
                    <template #prepend-inner>
                        <uv-button no-margin icon color="info" :title="$t('saveSearch')" @click.stop.native="saveQuery">
                            <v-icon size="28">$agent</v-icon>
                        </uv-button>
                    </template>
                </uv-autocomplete>
            </template>
            <template #status cols="3">
                <uv-autocomplete v-model="localQuery.status_id" :items="enumsItems.AgentVisitStatus" clearable
                    search-field small-chips chips deletable-chips multiple :label="$t('labels.status')" dense
                    @change="getActivities()">
                    <template #item="{ item }">
                        <v-list-item-icon>
                            <v-icon :color="item.icon?.color">{{ item.icon?.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>{{ item.text }}</v-list-item-content>
                    </template>
                    <template #prepend-inner>
                        <uv-button no-margin icon color="info" :title="$t('saveSearch')" @click="saveQuery">
                            <v-icon left size="28">{{ icons.mdiStateMachine }}</v-icon>
                        </uv-button>
                    </template>
                </uv-autocomplete>
            </template>
            <template #activity_id cols="3">
                <uv-autocomplete :label="$t('labels.type')" clearable search-field small-chips chips deletable-chips multiple
                    :items="enumsItems.AgentActivity" v-model="localQuery.activity_id" @change="getActivities()">
                    <template #prepend-inner>
                        <uv-button no-margin icon color="info" :title="$t('saveSearch')" @click="saveQuery">
                            <v-icon left size="28">{{ icons.mdiStateMachine }}</v-icon>
                        </uv-button>
                    </template>
                    <template #item="{ item }">
                        <v-list-item-icon>
                            <v-icon :color="item.icon?.color">{{ item.icon?.icon }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content>{{ item.text }}</v-list-item-content>
                    </template>
                </uv-autocomplete>
            </template>
            <template #priority_id>
                <uv-autocomplete  :label="$t('labels.priority')" clearable search-field small-chips chips deletable-chips multiple
                    :items="enumsItems.AgentVisitPriority" v-model="localQuery.priority_id" @change="getActivities()">
                    <template #prepend-inner>
                        <uv-button no-margin icon color="info" :title="$t('saveSearch')" @click="saveQuery">
                            <v-icon left size="28">{{ icons.mdiStateMachine }}</v-icon>
                        </uv-button>
                    </template>
                </uv-autocomplete>
            </template>
            <template #client_id>
                <uv-client-search-field v-model="localQuery.client_id"
                    :label="$t('labels.client')" clearable  search-field small-chips chips deletable-chips multiple
                    @change="getActivities()">
                    <template #prepend-inner>
                        <uv-button no-margin icon color="info" @click="saveQuery" :title="$t('labels.clientDetails')">
                            <v-icon size="28">$client</v-icon>
                        </uv-button>
                    </template>
                </uv-client-search-field>
            </template>
            <template #actions>
                <uv-button color="success" @click="openAddNewActivity">
                    <v-icon>$plus</v-icon>
                    {{ $t("labels.add") }}
                </uv-button>
            </template>
            <template #field-action="{ item }">
              
                <v-menu offset-x>
                    <template #activator="{ on, attrs }">
                        <uv-button class="ml-auto" v-on="on" v-bind="attrs" :color="item.color">
                        </uv-button>
                    </template>
                    <v-list>
                        <v-list-item v-for="(color, index) in colors" :key="index" :class="color"
                            @click="handleColorChange(item, color)"></v-list-item>
                    </v-list>
                </v-menu>
            </template>
        </uv-filtered-grid>
        <v-row class="flex-grow-1 d-flex flex-column" style="overflow: hidden">
            <v-col cols="12" class="d-flex flex-column" style="overflow: hidden">
                <v-sheet class="flex-grow-1 d-flex flex-column pa-1 rounded-lg" style="overflow: hidden">
                    <v-row class="flex-grow-0 elevation-2" style="z-index: 1" dense>
                        <v-col xs md="2" lg="1">
                            <uv-select :disabled="activityData.busy || visitRange.busy" :items="calendarTypeItems" v-model="calendarType" outlined dense hide-details
                                :label="$t('labels.calendarType')">
                            </uv-select>
                        </v-col>
                        <v-col xs md="2" lg="1">
                            <uv-select :disabled="activityData.busy || visitRange.busy" :items="calendarYearItems" v-model="calendarYear" @change="handleYearChange"
                                outlined dense hide-details :label="$t('year')">
                            </uv-select>
                        </v-col>
                        <v-col xs md="2" lg="1">
                            <uv-select :disabled="activityData.busy || visitRange.busy" :items="calendarMonthItems" v-model="calendarMonth" @change="handleMonthChange"
                                outlined dense hide-details :label="$t('month')">
                            </uv-select>
                        </v-col>
                        <v-col cols="auto" class="d-flex align-center">
                            <uv-button icon ref="button">
                                <v-icon color="info" large ref="info">
                                    $info
                                </v-icon>                                
                            </uv-button>
                                <v-tooltip right :activator="$refs.button">
                                    <v-container fluid>
                                        <v-row v-for="item in enumsItems.AgentActivity" :key="item.value">
                                            <v-col cols="auto">
                                                <v-icon :color="item.icon?.color">
                                                    {{ item.icon?.icon}}
                                                </v-icon>
                                            </v-col>
                                            <v-col cols="auto">
                                                {{ item.text }}
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-tooltip>
                        </v-col>
                        <v-col cols="auto" v-if="canImport" class="d-flex align-center">
                            <uv-button @click="importData.displayDialog = true" text outlined color="warning">
                                <v-icon left size="24">{{ icons.mdiArrowUpBoldBox }}</v-icon>
                                <span>XLS</span>
                            </uv-button>
                        </v-col>
                        <v-col cols="auto" class="ml-auto d-flex align-center">
                            <uv-button color="uv-cancel" icon text :title="$t('tooltips.refresh')" @click="handleReload" small :loading="activityData.busy">
                                <v-icon>{{ icons.mdiRefresh }}</v-icon>
                            </uv-button>
                            <v-item-group light class="elevation-2 mx-1 v-btn-toggle v-btn-toggle--dense rounded-xl overflow-hidden">
                                <uv-button @click="handlePrev" text small icon :disabled="activityData.busy" :title="$t('labels.goBack')">
                                    <v-icon>{{ icons.mdiChevronLeft }}</v-icon>
                                </uv-button>
                                <uv-button @click="handleNext" icon text small :disabled="activityData.busy" :title="$t('labels.goForward')">
                                    <v-icon>{{ icons.mdiChevronRight }}</v-icon>
                                </uv-button>
                            </v-item-group>
                        </v-col>
                    </v-row>
                    <v-container fluid class="flex-grow-1 mt-1 pa-0 calendar-container">
                        <v-overlay absolute v-if="activityData.busy || visitRange.busy" opacity="0.5">
                            <v-progress-circular color="info" indeterminate size="64"></v-progress-circular>
                        </v-overlay>
                        <v-calendar ref="calendar" v-model="calendarDate" :events="calendarEvents"
                            :type="calendarType" :weekdays="weekdays" @change="handleRangeChange"
                            :locale="this.$store.state.currentLanguage" @click:more="handleClickMore"
                            @click:event="handleClickEvent" @click:day="handleClickDay"
                            event-overlap-threshold="0" @mouseenter:event="handleEventHoverIn"
                            @mouseleave:event="handleEventHoverOut" interval-height="100" class="uv-calendar">
                            <template #event="{ event }">
                                <v-icon small>
                                    {{ enumsItems.AgentVisitStatus.find(status => status.value ===
                                            event.status_id)?.icon.icon
                                    }}
                                </v-icon>
                                <v-icon small>
                                    {{ enumsItems.AgentActivity.find(activity => activity.value ===
                                            event.agent_activity_id)?.icon.icon
                                    }}
                                </v-icon>
                                {{ event.client?.denumire }},
                                {{ event.user?.name }}
                            </template>
                        </v-calendar>
                    </v-container>
                </v-sheet>
            </v-col>
        </v-row>
        <activity-details ref="activityDialog" :activityId="selectedActivityId" :mode="activityMode" @shouldReload="handleReload" :defaultDate="activityDefaultDate"/>
        <v-dialog v-model="importData.displayDialog" persistent content-class="fill-height overflow-hidden">
            <v-card class="fill-height d-flex flex-column">
                <v-toolbar :dark="!$vuetify.theme.dark" :light="$vuetify.theme.dark" class="flex-grow-0">
                    <v-toolbar-title>{{ $t("labels.uploadData") }}</v-toolbar-title>
                    <v-spacer></v-spacer>
                    <uv-button icon :color="!$vuetify.theme.dark ? 'white' : 'uv-cancel'" @click="importData.displayDialog = false; importData.shouldReset = true">
                        <v-icon>$close</v-icon>
                    </uv-button>
                </v-toolbar>
                <v-card-text class="flex-grow-1 overflow-hidden pa-0">
                    <activities-import class="fill-height overflow-y-auto" :should-reset.sync="importData.shouldReset" @cancel="importData.displayDialog = false; importData.shouldReset = true"></activities-import>
                </v-card-text>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script type="text/javascript">
import { mdiMapMarker, mdiChevronLeft, mdiChevronRight, mdiStateMachine, mdiArrowUpBoldBox, mdiRefresh} from "@mdi/js";
import _ from "lodash";
import { enumsMixin } from "@/plugins/mixins";
import {isNullOrUndefined} from "@/plugins/utils";
import ActivityDetails from "@/views/Activities/ActivityDetails";
import ActivitiesImport from "@/views/Activities/ActivitiesImport";
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import { queryParamsMixin } from "@/plugins/mixins";
import { DateTime } from "luxon";
export default {
    name: "Activities",
    mixins: [enumsMixin, queryParamsMixin],
    components: { ActivityDetails, ActivitiesImport },
    data() {
        return {
            importData: {
                displayDialog: false,
                shouldReset: false
            },
            icons: {
                mdiMapMarker,
                mdiChevronLeft,
                mdiChevronRight,
                mdiStateMachine,
                mdiArrowUpBoldBox,
                mdiRefresh
            },
            calendarType: "month",
            weekdays: [1, 2, 3, 4, 5, 6, 0],
            calendarDate: DateTime.now().toFormat("yyyy-MM-dd"),
            calendarStartDate: null,
            calendarEndDate: null,
            calendarYear: null,
            calendarMonth: null,
            activityData: {
                items: [],
                busy: false,
                abortController: null
            },
            visitRange: {
                startDate: DateTime.fromObject({year:2010,month:1,day:1}),
                endDate:DateTime.fromObject({year:2025,month:12,day:31}),
                busy: false,
                abortController: null
            },
            enumNames: ["AgentActivity", "AgentVisitStatus", "UserRole"],
            selectedActivityId: null,
            activityDefaultDate:null,
            workplaceData: {
                items: [],
                busy: false,
                abortController: null,
            },
            agentData: {
                items: [],
                busy: false,
                abortController: null,
            },
            colors: [
                "accent",
                "success",
                "purple",
                "info",
                "warning"
            ],
            activityConfigName: "activityView",
            startYear: 2010,
            endYear: 2024,
            activityMode:"view"
        }
    },
    created() {
        this.loadConfigs();
        this.getActivities = _.debounce(function (limit) {
            let url = "/agent_visits";
            let params = {
                ...this.localQuery,
                date_start: this.calendarStartDate,
                date_stop: this.calendarEndDate
            };
            if (limit) params = { limit };
            if (this.activityData.busy) {
                this.activityData.abortController?.abort();
            }
            this.activityData.busy = true;
            this.activityData.abortController = new AbortController();
            this.$apiHttp({
                url,
                signal: this.activityData.abortController.signal,
                params
            })
                .then((res) => {
                    if (res.data.data?.length) {
                        this.activityData.items = res.data.data;
                    } else {
                        this.activityData.items = [];
                    }
                })
                .finally(() => {
                    this.activityData.busy = false
                });
        }, 500);

        let keys = ["status_id"];

        for (let key of keys) {
            if (typeof this.localQuery[key] === "string") {
                this.localQuery[key] = [this.localQuery[key]];
            }
        }

        keys = ["workplace_id", "agent_id"];
        for (let key of keys) {
            if (!this.localQuery[key]) {
                continue
            }
            if (typeof this.localQuery[key] === "string") {
                this.localQuery[key] = [this.localQuery[key]]
            }
            this.localQuery[key] = this.localQuery[key].map(k => parseInt(k))
        }
        this.getVisitRange().then(() => this.getActivities());
        if (this.localQuery.agent_id) this.getAgents();
        if (this.localQuery.workplace_id) this.getWorkplaces();
        this.setShowNavSearch(true);
    },
    beforeDestroy() {
        if (this.activityData.busy) this.activityData.abortController.abort();
        if (this.workplaceData.busy) this.workplaceData.abortController.abort();
        if (this.agentData.busy) this.agentData.abortController.abort();
        this.setShowNavSearch(false);
    },
    computed: {
        ...mapState(["currentUser"]),
        ...mapState(["activityDetails",["reloadData"]]),
        ...mapGetters("config", ["getConfig", "getConfigProfile", "getSelectedProfile"]),
        canImport() {
            return [this.enums.UserRole?.ADMIN, this.enums.UserRole?.DERULARE].includes(this.currentUser?.active_role?.role_id);
        },
        filterFields() {
            if (this.isAdmin)
                return {
                    workplace: {
                        name: "workplace",
                        label: this.$t('labels.location')
                    },
                    agent: {
                        name: "agent",
                        label: this.$t('labels.inCharge')
                    },
                    status: {
                        name: "status",
                        label: this.$t('labels.status')
                    },
                    activity:{
                        name:"activity_id",
                        label:this.$t('labels.activity')
                    },
                    priority:{
                        name:"priority_id",
                        label:this.$t('labels.priority')
                    },
                    client:{
                        name:"client_id",
                        label:this.$t('labels.client')
                    }
                }
            return {
                workplace: {
                    name: "workplace",
                    label: this.$t('labels.location')
                },
                status: {
                    name: "status",
                    label: this.$t('labels.status')
                }
            }
        },
        selectedProfileName() {
            let selectedProfile = this.getSelectedProfile(this.activityConfigName);
            return selectedProfile?.name ?? 'default';
        },
        calendarTypeItems() {
            return [
                {
                    text: this.$t('enums.CalendarViewType.month'),
                    value: 'month'
                },
                {
                    text: this.$t('enums.CalendarViewType.week'),
                    value: 'week'
                },
                {
                    text: this.$t('enums.CalendarViewType.4day'),
                    value: '4day'
                },
                {
                    text: this.$t('enums.CalendarViewType.day'),
                    value: 'day'
                },
            ]
        },
        calendarYearItems() {
            return _.range(this.visitRange.startDate.year, this.visitRange.endDate.year + 1).reverse();
        },
        calendarMonthItems() {
            return [{
                text: this.$t('months.january'),
                value: 0
            }, {
                text: this.$t('months.february'),
                value: 1
            }, {
                text: this.$t('months.march'),
                value: 2
            }, {
                text: this.$t('months.april'),
                value: 3
            }, {
                text: this.$t('months.may'),
                value: 4
            }, {
                text: this.$t('months.june'),
                value: 5
            }, {
                text: this.$t('months.july'),
                value: 6
            }, {
                text: this.$t('months.august'),
                value: 7
            }, {
                text: this.$t('months.september'),
                value: 8
            }, {
                text: this.$t('months.october'),
                value: 9
            }, {
                text: this.$t('months.november'),
                value: 10
            }, {
                text: this.$t('months.december'),
                value: 11
            },]
        },
        calendarEvents() {
            return this.activityData.items.map(item => {
                return {
                    ...item,
                    start: DateTime.fromSQL(`${item.date} ${item.time}`).ts,
                    end: DateTime.fromSQL(`${item.date} ${item.time}`).ts + 1000 * 60 * 10,
                    color: this.getSelectedProfile(`${this.currentUser.id}${this.activityConfigName}`)?.items?.find(config => config.name === item.agent_activity_id)?.color ??
                        this.enumsItems.AgentActivity.find(type => type.value === item.agent_activity_id)?.icon.color,
                    timed: true
                }
            });
        },
        isAdmin() {
            return !isNullOrUndefined(this.currentUser?.active_role) && 
                this.currentUser.active_role?.role_id == this.enums?.UserRole?.ADMIN;
        },
        colorItems() {
            return this.enumsItems.AgentActivity?.map(item => ({ name: item.value, color: item.icon?.color, label: this.$t(`enums.AgentActivity.${item.value}`) }));
        },
    },
    methods: {
        ...mapMutations("config", ["setSelectedProfile", "saveConfigs"]),
        ...mapMutations({ setShowNavSearch: "SET_showNavSearch"}),
        ...mapMutations("activityDetails",['setReloadData']),
        ...mapActions("config", ["setConfigProfile", "loadConfigs", "deleteConfigProfile"]),
        handleReload(){
            this.setReloadData({isSilent:true,shouldReload:true});
        },
        openAddNewActivity(){
            this.activityDefaultDate = null;
            if(this.calendarType === "day"){
                this.activityDefaultDate = this.calendarDate;
            }
            this.selectedActivityId = null;
            this.activityMode = "add";
            this.$refs.activityDialog.open();
        },
        getAgents() {
            if (this.agentData.items.length) return;
            if (this.agentData.busy) {
                this.agentData.abortController?.abort();
            }
            this.agentData.busy = true;
            this.agentData.abortController = new AbortController();
            this.$apiHttp({
                url: "/users/agents",
                signal: this.agentData.abortController.signal,
            }).then((res) => {
                if (res.data.data) this.agentData.items = res.data.data.map((agent) => {
                    return {
                        text: agent.name,
                        value: agent.id,
                    };
                });
            }).finally(() => this.agentData.busy = false);
        },
        getWorkplaces() {
            if (this.workplaceData.items.length) return;
            if (this.workplaceData.busy) {
                this.workplaceData.abortController?.abort();
            }
            this.workplaceData.busy = true;
            this.workplaceData.abortController = new AbortController();
            this.$apiHttp({
                url: "/workplaces",
                signal: this.workplaceData.abortController.signal,
            }).then((res) => {
                if (res.data.data) this.workplaceData.items = res.data.data.map((workplace) => {
                    return {
                        text: workplace.denumire,
                        value: workplace.id,
                    };
                });
            }).finally(() => this.workplaceData.busy = false);
        },
        getVisitRange() {
            return new Promise((resolve, reject) => {
                if (this.visitRange.busy) {
                    this.visitRange.abortController?.abort();
                }
                this.visitRange.busy = true;
                this.visitRange.abortController = new AbortController();
                this.$apiHttp({
                    url: "/agent_visits/visits_range",
                    signal: this.visitRange.abortController.signal,
                }).then((res) => {
                    if (res.data.data?.start_date && res.data.data?.end_date) {
                        this.visitRange.startDate = DateTime.fromISO(res.data.data.start_date);
                        this.visitRange.endDate = DateTime.fromISO(res.data.data.end_date);
                        this.calendarYear = this.visitRange.endDate.year;
                        this.handleMonthChange(this.visitRange.endDate.month-1)
                        resolve();
                    }
                }).catch(() => reject())
                .finally(() => this.visitRange.busy = false);
            })
        },
        handleEventHoverIn({ nativeEvent }) {
            nativeEvent.target.style.zIndex = 1;
        },
        handleEventHoverOut({ nativeEvent }) {
            nativeEvent.target.style.zIndex = 0;
        },
        handleColorChange(item, color) {
            item.color = color;
        },
        handlePrev() {
            this.$refs.calendar.prev();
        },
        handleNext() {
            this.$refs.calendar.next();
        },
        handleRangeChange({ start, end }) {
            this.calendarYear = start.year;
            this.calendarMonth = start.month - 1;
            this.calendarStartDate = start.date;
            this.calendarEndDate = end.date;
        },
        handleYearChange(newYear) {
            this.calendarDate = DateTime.fromISO(`${newYear}-${DateTime.fromObject({month:this.calendarMonth+1}).toFormat("MM")}-01`).toFormat("yyyy-MM-dd");
        },
        handleMonthChange(newMonth) {
            this.calendarDate = DateTime.fromISO(`${this.calendarYear}-${DateTime.fromObject({month:newMonth+1}).toFormat("MM")}-01`).toFormat("yyyy-MM-dd");
        },
        handleClickMore({ nativeEvent, date }) {
            this.calendarDate = date;
            this.calendarType = "day";
            nativeEvent.stopPropagation();
        },
        handleClickDay({ nativeEvent, date }) {
            this.calendarDate = date;
            this.calendarType = "day";
            nativeEvent.stopPropagation();
        },
        handleClickEvent({ nativeEvent, event }) {
            this.activityDefaultDate = null;
            this.selectedActivityId = event.id;
            this.activityMode = "edit";
            this.$refs.activityDialog.open();
            nativeEvent.stopPropagation();
        }
    },
    watch: {
        currentUser(newVal, oldVal) {
            if (oldVal?.active_role?.role_id && newVal && newVal.active_role?.role_id !== oldVal?.active_role?.role_id) {
                this.getVisitRange().then(() => this.getActivities());
            }
        },
        calendarStartDate() {
            if (!isNullOrUndefined(this.currentUser?.active_role_id)) {
                this.getActivities();
            }
        },
        calendarEndDate() {
            if (!isNullOrUndefined(this.currentUser?.active_role_id)) {
                this.getActivities();
            }
        },
        'activityDetails.reloadData.shouldReload': function(newVal){
            if(newVal){
                this.getActivities();
                this.setReloadData({shouldReload:false, isSilent:false})
            }
        }
    }
}
</script>

<style lang="scss" scoped>
:deep(.search-field) {
    .v-text-field--rounded>.v-input__control>.v-input__slot>.v-input__prepend-inner {
        margin-left: -16px;
    }
}

.v-icon {
    color: unset;
}

.v-calendar-monthly {
    @media (max-height: 900px) {
        height: 150%;
    }
}

.sticky-header {
    position: sticky; 
    top: 0;
    z-index: 3;
}

.calendar-container {
    margin-right: -3px;
    overflow-y: auto;
    position: relative;
    .uv-calendar {
        padding: 0 1px 1px 0;
    }
}
</style>